import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CATEGORY_COLORS, formatSeconds, competitionResultLabel, isHistoricalResult } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { ArrowLeft, Calendar, MapPin, Trophy, User } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function ChildProfile() {
  const { CATEGORY_LABEL } = useCategories();
  const { id } = useParams();
  const [child, setChild] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [retry, setRetry] = useState(0);
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true); setNotFound(false); setLoadError("");
    setChild(null); setComps([]); setRegistrations([]);
    api.get(`/parent/children/${id}`)
      .then((r) => {
        if (!active) return;
        setChild(r.data.child); setComps(r.data.competitors || []); setRegistrations(r.data.registrations || []);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadError("Could not load this cuber's profile. Please try again.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, retry]);

  if (loading) return <div className="p-12 text-center text-[#7A7D8C]" data-testid="child-profile-loading">Loading…</div>;
  if (loadError) return <div role="alert" className="p-6 text-sm text-red-800" data-testid="child-profile-error">{loadError}<button onClick={() => setRetry((value) => value + 1)} className="ml-2 underline" data-testid="child-profile-retry">Try again</button></div>;
  if (notFound) return (
    <div className="max-w-xl p-12 text-center">
      <h1 className="font-display font-black text-3xl">Cuber not found</h1>
      <p className="mt-3 text-[#7A7D8C]">This profile doesn't exist under your account.</p>
      <Link to="/parent/cubers" className="mt-6 inline-flex items-center gap-2 text-[#FF9F1C] font-semibold"><ArrowLeft className="w-4 h-4" /> Back to My Cubers</Link>
    </div>
  );

  return (
    <div className="max-w-4xl">
      <Link to="/parent/cubers" data-testid="child-back" className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C] hover:text-[#FF9F1C] inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back to My Cubers</Link>

      <div className="mt-4 flex items-start gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-[#FFBF69]/25 grid place-items-center text-[#B36906] shrink-0"><User className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="child-profile-name">{child?.full_name}</h1>
          {child?.current_school && <p className="mt-1 text-[#7A7D8C]">{child.current_school}</p>}
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <Stat label="Default category" value={child?.parent_selected_category ? CATEGORY_LABEL[child.parent_selected_category] : "—"} accent={child?.parent_selected_category} />
        <Stat label="Shirt size" value={child?.shirt_size || "—"} />
        <Stat label="Registered entries" value={comps.length + registrations.length} />
      </div>

      {registrations.length > 0 && <section className="mt-8" data-testid="child-registration-list"><h2 className="font-display font-bold text-lg">Registrations</h2><div className="mt-3 space-y-3">{registrations.map((registration) => <div key={registration.id} className="rounded-2xl border border-slate-100 bg-white p-4 flex flex-wrap justify-between gap-3" data-testid={`child-registration-${registration.id}`}><div><div className="font-semibold">{registration.event?.title || "Event registration"}</div><p className="mt-1 text-xs text-[#7A7D8C]">{registration.event?.start_date || "Date unavailable"}</p></div><span className="text-xs font-semibold text-[#B36906]" data-testid={`registration-status-${registration.id}`}>Payment: {registration.payment_status || "pending"}</span></div>)}</div></section>}

      <h2 className="mt-10 font-display font-black text-lg tracking-tight">Competition history</h2>
      {comps.length === 0 ? (
        <div className="mt-4 rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center text-[#7A7D8C]" data-testid="child-history-empty">
          No competition results yet. Once {child?.full_name || "your cuber"} competes, their stats will show here automatically.
        </div>
      ) : (
        <div className="mt-4 space-y-4" data-testid="child-history-list">
          {comps.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-100 p-6" data-testid={`history-${r.id}`}>
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  {r.event && (
                    <>
                      <div className="font-display font-bold text-lg">{r.event.title}</div>
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-[#7A7D8C]">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(r.event.start_date)}</span>
                        {r.event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {r.event.location}</span>}
                      </div>
                    </>
                  )}
                  {!r.event && <div className="font-display font-bold text-lg">Unlinked competition entry</div>}
                </div>
                {r.assigned_category && (
                  <span className={`text-[11px] font-bold border px-2 py-0.5 rounded-full ${CATEGORY_COLORS[r.assigned_category] || ""}`}>{CATEGORY_LABEL[r.assigned_category]}</span>
                )}
              </div>
              {r.placement != null && <p className="mt-3 text-sm font-semibold text-[#B36906]" data-testid={`history-placement-${r.id}`}>Category placement: #{r.placement}</p>}
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <Kv label="Timing Solve" value={formatSeconds(r.prelim_average)} />
                <Kv label="Recommended" value={r.recommended_category ? CATEGORY_LABEL[r.recommended_category] : "—"} />
                <Kv label="Assigned" value={r.assigned_category ? CATEGORY_LABEL[r.assigned_category] : "—"} />
                <Kv label={competitionResultLabel(r)} value={r.official_dnf ? "DNF" : formatSeconds(r.official_average)} tid={`history-result-${r.id}`} />
              </div>
              {(r.official_solves || []).some((x) => x != null) && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]"><Trophy className="w-3.5 h-3.5 inline -mt-0.5" /> {isHistoricalResult(r) ? "Historical solves" : "Competition solve"}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(r.official_solves || []).map((s, i) => (
                      <span key={`${r.id}-solve-${i}`} className="px-3 py-1 rounded-full bg-[#FDFFFC] border border-slate-100 text-sm font-mono">{s == null ? "DNF" : formatSeconds(s)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#7A7D8C] font-semibold">{label}</div>
      <div className={`mt-1 font-display font-black ${accent ? "text-[#B36906]" : ""}`}>{value}</div>
    </div>
  );
}

function Kv({ label, value, tid }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-[#FDFFFC] p-3" data-testid={tid}>
      <div className="text-[10px] uppercase tracking-wider text-[#7A7D8C] font-semibold">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}
